import _flatten from 'lodash/flatten';
import { useApolloClient, useQuery, useSubscription } from '@apollo/client';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import SaleCarousel from '../components/SaleCarousel';
import queryDefs from '../lib/queryDefs';
import getSaleCarouselPropsFromCollection from '../lib/getSaleCarouselPropsFromCollection';

export default function Home() {
  const client = useApolloClient();

  const { data, error, loading } = useQuery(queryDefs.marketHome, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5 * 60 * 1000, // 5 minutes
    onCompleted: () => {
      client.refetchQueries({ include: ['SalesEndingSoonRealtimeSync'] });
    },
  });

  const allSalesOnPage = [
    ...new Set(
      _flatten(
        data?.collections?.map(collection => collection.sales.items) ?? [],
      ),
    ),
  ];

  const auctionIds = allSalesOnPage.filter(sale => sale.saleType === 'auction');

  useQuery(queryDefs.salesRealtimeSync, {
    variables: {
      ids: auctionIds.map(sale => sale.id),
    },
    skip: !auctionIds.length,
  });

  useSubscription(queryDefs.saleUpdated, {
    variables: { saleIds: auctionIds.map(sale => sale.id) },
    skip: !auctionIds.length,
  });

  if (!data && loading) {
    // Add a huge bottom padding to avoid bumping user to bottom of page due to large footer on namecheap.com
    return <Loader className="tw-pt-48 tw-pb-[9999px]" />;
  }

  if (!data && error) {
    // Only if no data, to avoid showing errors on stale browser tabs
    return (
      <div className="tw-px-4">
        <div className="tw-mx-auto tw-max-w-screen-2xl tw-my-4">
          <Alert severity="error" title="Failed to load data" />
        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-near-white tw-py-1">
      <Meta
        canonical="https://www.namecheap.com/market/"
        description="Namecheap Market is the place to buy and sell domain names at auction. Buy domains instantly or bid in a domain auction to grab the perfect domain name."
        robots="all"
        title="Buy &amp; Sell Domains - Domain Name Auctions | Namecheap"
      />
      {Object.values(data?.collections || {})
        .filter(collection => !collection.hiddenOnWeb)
        .map(collection => (
          <SaleCarousel
            className="tw-my-10 xl:tw-my-12"
            containerClassName="tw-grid-cols-[1fr_min(100%,1440px)_1fr] tw-px-4"
            key={collection.slug}
            {...getSaleCarouselPropsFromCollection(collection)}
          />
        ))}
    </div>
  );
}
